import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChartLine,
  PencilRuler,
  HardHat,
  Building,
  Download,
  Edit,
  Save,
  Plus,
  Trash2,
  Lock
} from 'lucide-react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, where, serverTimestamp, addDoc, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getDownloadURL, getBytes } from 'firebase/storage';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import StartSidebar from '../components/StartSidebar';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const StartKPIs = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('design');
  const [selectedKPI, setSelectedKPI] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedKPI, setEditedKPI] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [kpis, setKpis] = useState({
    design: [],
    construction: [],
    office: []
  });
  const [userCustomizations, setUserCustomizations] = useState({});
  const [unlockedKPIs, setUnlockedKPIs] = useState(null); // null means not set, array means explicitly set
  const modalRef = useRef(null);

  const tabs = [
    { id: 'design', label: 'Design', icon: PencilRuler },
    { id: 'construction', label: 'Construction', icon: HardHat },
    { id: 'office', label: 'Office', icon: Building }
  ];

  // Fetch KPIs from Firestore and user customizations
  useEffect(() => {
    if (currentUser) {
      fetchKPIs();
      fetchUserCustomizations();
    }
  }, [currentUser]);

  const fetchKPIs = async () => {
    try {
      // Get user's accountId from their user document
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let accountId = null;
      let unlockedKPIsList = null; // null means not set, array means explicitly set
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        accountId = userData.accountId;
        
        // Fetch account data to get unlockedKPIs
        if (accountId) {
          try {
            const accountData = await getAccountById(accountId);
            if (accountData && accountData.unlockedKPIs !== undefined) {
              unlockedKPIsList = Array.isArray(accountData.unlockedKPIs) ? accountData.unlockedKPIs : [];
            }
          } catch (error) {
            console.error('Error fetching account data:', error);
            // Continue without unlockedKPIs filter if account fetch fails
          }
        }
      }

      // Store unlockedKPIs in state for checking during render
      setUnlockedKPIs(unlockedKPIsList);

      // Fetch all KPI templates
      const templateQuerySnapshot = await getDocs(collection(db, 'kpiTemplates'));
      const templateKPIs = templateQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch account-specific KPI jobs if accountId exists
      let accountKPIs = [];
      if (accountId) {
        try {
          const accountKPIsQuerySnapshot = await getDocs(collection(db, 'accounts', accountId, 'kpiJobs'));
          accountKPIs = accountKPIsQuerySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific KPIs:', error);
          // Continue with just template KPIs if account KPIs fail to load
        }
      }

      // Group KPIs by department - extract jobs/roles from templates
      const groupedKPIs = {
        design: [],
        construction: [],
        office: []
      };

      // Process KPI templates - extract jobs/roles from departmentKPIs
      templateKPIs.forEach(template => {
        if (template.departmentKPIs) {
          // Process each department
          ['design', 'construction', 'office'].forEach(dept => {
            if (template.departmentKPIs[dept] && Array.isArray(template.departmentKPIs[dept])) {
              template.departmentKPIs[dept].forEach((job, jobIndex) => {
                // Create a unique ID for each job based on template ID, department, and index
                const jobId = `${template.id}_${dept}_${jobIndex}`;
                groupedKPIs[dept].push({
                  id: jobId,
                  templateId: template.id,
                  title: job.title || job.role || `Job ${jobIndex + 1}`,
                  description: template.description || '',
                  department: dept,
                  kpis: job.kpis || [],
                  companyGoals: template.companyGoals || [],
                  value: job.value || 'View Details',
                  ...job,
                  isFromTemplate: true
                });
              });
            }
          });
        }
      });

      // Add account-specific KPI jobs
      accountKPIs.forEach(kpiJob => {
        if (kpiJob.department && groupedKPIs[kpiJob.department]) {
          groupedKPIs[kpiJob.department].push({
            id: kpiJob.id,
            title: kpiJob.title || kpiJob.role || 'Untitled Role',
            description: '',
            department: kpiJob.department,
            kpis: kpiJob.kpis || [],
            value: kpiJob.value || 'View Details',
            ...kpiJob,
            isAccountSpecific: true
          });
        }
      });
      
      setKpis(groupedKPIs);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  };

  const fetchUserCustomizations = async () => {
    try {
      // Get all user customizations
      const customizationsQuery = query(
        collection(db, 'userKPICustomizations'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(customizationsQuery);
      const customizations = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.templateId) {
          customizations[data.templateId] = {
            id: doc.id,
            ...data
          };
        }
      });
      
      setUserCustomizations(customizations);
    } catch (error) {
      console.error('Error fetching user customizations:', error);
    }
  };

  const handleKPIClick = useCallback((job, event) => {
    // Prevent event propagation to avoid interference from other handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate job data
    if (!job || !job.id) {
      console.error('Invalid job data:', job);
      alert('Error: Invalid job data. Please try again.');
      return;
    }
    
    try {
      // Check if user has customizations for the template
      const templateId = job.templateId;
      const userCustomization = templateId ? userCustomizations[templateId] : null;
      
      // Start with the job data and ensure all required fields exist
      let displayJob = {
        id: job.id,
        title: job.title || job.role || 'Untitled Role',
        description: job.description || '',
        department: job.department || activeTab,
        kpis: job.kpis || [],
        companyGoals: job.companyGoals || [],
        ...job
      };
      
      // If user has customizations, try to find the customized version of this job
      if (userCustomization && userCustomization.departmentKPIs) {
        const customizedDept = userCustomization.departmentKPIs[displayJob.department];
        if (customizedDept && Array.isArray(customizedDept)) {
          // Find the job by matching title or jobId
          const customizedJob = customizedDept.find(j => 
            j.title === displayJob.title || j.jobId === displayJob.jobId || j.role === displayJob.title
          );
          if (customizedJob) {
            displayJob = {
              ...displayJob,
              title: customizedJob.title || displayJob.title,
              kpis: customizedJob.kpis || displayJob.kpis,
              hasCustomizations: true
            };
          }
        }
      }
      
      // Store the original template ID for saving customizations
      displayJob.templateId = templateId;
      displayJob.hasCustomizations = displayJob.hasCustomizations || false;
      
      setSelectedKPI(displayJob);
      setEditedKPI(displayJob);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error handling job click:', error);
      alert('Error opening job. Please try again.');
    }
  }, [userCustomizations, activeTab]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const templateId = selectedKPI.templateId;
      if (!templateId) {
        alert('Error: No template ID found. Cannot save customization.');
        return;
      }

      // Get existing customizations or start fresh
      const existingCustomization = userCustomizations[templateId];
      let existingDepartmentKPIs = existingCustomization?.departmentKPIs || {
        design: [],
        construction: [],
        office: []
      };

      // Update only the specific job that was edited
      const updatedDepartmentKPIs = {
        ...existingDepartmentKPIs,
        [editedKPI.department]: existingDepartmentKPIs[editedKPI.department].map(job => 
          job.id === selectedKPI.id || job.jobId === selectedKPI.id ? editedKPI : job
        )
      };

      // If this job doesn't exist in customizations yet, add it
      const existingJob = existingDepartmentKPIs[editedKPI.department].find(job => 
        job.id === selectedKPI.id || job.jobId === selectedKPI.id
      );
      if (!existingJob) {
        updatedDepartmentKPIs[editedKPI.department] = [
          ...updatedDepartmentKPIs[editedKPI.department],
          editedKPI
        ];
      }

      const customizationData = {
        userId: currentUser.uid,
        templateId: templateId,
        departmentKPIs: updatedDepartmentKPIs,
        updatedAt: serverTimestamp()
      };

      if (existingCustomization) {
        // Update existing customization
        await updateDoc(doc(db, 'userKPICustomizations', existingCustomization.id), customizationData);
      } else {
        // Create new customization
        const docRef = await addDoc(collection(db, 'userKPICustomizations'), {
          ...customizationData,
          createdAt: serverTimestamp()
        });
        
        // Update local customizations state
        setUserCustomizations(prev => ({
          ...prev,
          [templateId]: {
            id: docRef.id,
            ...customizationData
          }
        }));
      }

      // Refresh customizations to get updated data
      await fetchUserCustomizations();
      
      // Update local state to show the customized version
      setSelectedKPI({
        ...editedKPI,
        hasCustomizations: true
      });
      
      // Update the KPIs display to show customized version
      setKpis(prev => ({
        ...prev,
        [editedKPI.department]: prev[editedKPI.department].map(job => 
          job.id === selectedKPI.id ? {
            ...job,
            title: editedKPI.title,
            kpis: editedKPI.kpis
          } : job
        )
      }));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving job customization:', error);
      alert('Error saving job customization. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedKPI({ ...selectedKPI });
    setIsEditing(false);
  };

  const handleResetToTemplate = async () => {
    try {
      const templateId = selectedKPI.templateId;
      if (!templateId) return;

      const customization = userCustomizations[templateId];
      if (customization?.departmentKPIs) {
        // Remove this job from customizations
        const updatedDepartmentKPIs = {
          ...customization.departmentKPIs,
          [selectedKPI.department]: customization.departmentKPIs[selectedKPI.department].filter(
            job => job.id !== selectedKPI.id && job.jobId !== selectedKPI.id
          )
        };
        
        // Update the customization in Firestore
        await updateDoc(doc(db, 'userKPICustomizations', customization.id), {
          departmentKPIs: updatedDepartmentKPIs,
          updatedAt: serverTimestamp()
        });
        
        // Refresh customizations
        await fetchUserCustomizations();
        
        // Reset the job to template version
        const templateJob = kpis[selectedKPI.department].find(job => 
          job.id === selectedKPI.id && !job.hasCustomizations
        );
        if (templateJob) {
          const resetJob = { ...templateJob, hasCustomizations: false };
          setSelectedKPI(resetJob);
          setEditedKPI(resetJob);
        }
      }
    } catch (error) {
      console.error('Error resetting to template:', error);
      alert('Error resetting to template. Please try again.');
    }
  };

  const renderDepartmentKPIs = (kpis) => {
    if (!kpis || kpis.length === 0) {
      return <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No KPIs defined for this role.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {kpis.map((kpi, kpiIndex) => (
          <div key={kpiIndex} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-base font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {kpi.title || kpi.name || `KPI ${kpiIndex + 1}`}
            </h4>
            <div className="text-center">
              <div className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {kpi.value || 'Not set'}
              </div>
              {kpi.period && (
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {kpi.period}
                </div>
              )}
              {kpi.formula && (
                <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Formula: {kpi.formula}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompanyGoals = (companyGoals) => {
    if (!companyGoals || companyGoals.length === 0) {
      return <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No company goals defined.</p>;
    }

    return (
      <div className="space-y-2">
        {companyGoals.map((goal, index) => (
          <div key={index} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-3 border`}>
            <div className="flex justify-between items-center">
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {goal.label || `Goal ${index + 1}`}
              </span>
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {goal.value || 'Not set'} {goal.period ? `(${goal.period})` : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className={`min-h-screen p-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-lg p-6">
            <div className="flex items-center justify-between mb-8">
              <BackButton onClick={() => navigate('/start/operations')} />
              <div className="flex items-center">
                <ChartLine className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Key Performance Indicators</h1>
              </div>
              <div className="w-[120px] flex justify-end">
                <AddButton onClick={() => {}} disabled />
              </div>
            </div>

            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
            {/* Tabs */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
              <nav className="-mb-px flex justify-center space-x-8" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                        ${activeTab === tab.id
                          ? 'border-red-500 text-red-600'
                          : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <Icon className="w-4 h-4 mr-2" />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {kpis[activeTab] && kpis[activeTab].length > 0 ? (
                kpis[activeTab].map((job, index) => {
                  // Get the display title
                  const displayTitle = job.title || job.role || 'Untitled Role';
                  const templateId = job.templateId;
                  const hasCustomizations = templateId ? !!userCustomizations[templateId] : false;
                  
                  // Check if this job/role is unlocked
                  // If unlockedKPIs is null (not set), all KPIs are unlocked (backward compatibility)
                  // If unlockedKPIs is an array, only jobs in that array are unlocked
                  const isUnlocked = unlockedKPIs === null || (Array.isArray(unlockedKPIs) && unlockedKPIs.includes(job.id));
                  
                  return (
                    <div
                      key={job.id || index}
                      className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} border rounded-lg p-6 transition-shadow duration-200 relative ${
                        isUnlocked 
                          ? 'hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer' 
                          : 'opacity-60 cursor-not-allowed'
                      }`}
                      onClick={(event) => {
                        if (!isUnlocked) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        // Add a small delay to ensure the click is processed
                        setTimeout(() => handleKPIClick(job, event), 0);
                      }}
                      onMouseDown={(event) => {
                        if (!isUnlocked) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        // Prevent default to avoid any interference
                        event.preventDefault();
                      }}
                    >
                      {/* Lock icon for locked jobs */}
                      {!isUnlocked && (
                        <div className="absolute top-3 right-3">
                          <Lock className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                      )}
                    <div className="flex flex-col items-center mb-4">
                      <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{displayTitle}</h3>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {hasCustomizations ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                          }`}>
                            Tailored
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                          }`}>
                            Template
                          </span>
                        )}
                      </div>
                    </div>
                    <button 
                      className={`w-full py-1.5 px-3 rounded-md transition-colors duration-200 text-sm ${
                        isUnlocked
                          ? darkMode 
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : 'bg-red-600 text-white hover:bg-red-700'
                          : darkMode
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={!isUnlocked}
                    >
                      {isUnlocked ? 'View Details' : 'Locked'}
                    </button>
                  </div>
                  );
                })
              ) : (
                <div className={`col-span-full text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="text-lg">No KPIs found for this department.</p>
                  <p className="text-sm mt-2">Create KPIs in the Templates section first.</p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedKPI && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div ref={modalRef} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto`}>
            <div className="p-4 sm:p-6 md:p-8">
              {(() => {
                try {
                  return (
                    <>
                      <div className="flex flex-col items-center mb-6 sm:mb-8">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedKPI.title}
                            onChange={(e) => setEditedKPI({ ...editedKPI, title: e.target.value })}
                            className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center border rounded px-2 py-1 w-full ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        ) : (
                          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedKPI.title}</h2>
                        )}
                      </div>

                      <div className="space-y-4 sm:space-y-6">
                        {/* Description */}
                        <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 sm:p-6 border`}>
                          <h3 className={`text-base sm:text-lg font-semibold mb-2 sm:mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Description</h3>
                          {isEditing ? (
                            <textarea
                              value={editedKPI.description || ''}
                              onChange={(e) => setEditedKPI({
                                ...editedKPI,
                                description: e.target.value
                              })}
                              rows={4}
                              className={`w-full resize-y border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-red-500`}
                              placeholder="Enter description..."
                            />
                          ) : (
                            <p className={`text-sm sm:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed break-words`}>
                              {selectedKPI.description || 'No description provided'}
                            </p>
                          )}
                        </div>

                        {/* Company Goals */}
                        {selectedKPI.companyGoals && selectedKPI.companyGoals.length > 0 && (
                          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 sm:p-6 border`}>
                            <h3 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Company Goals</h3>
                            {renderCompanyGoals(selectedKPI.companyGoals)}
                          </div>
                        )}

                        {/* Department KPIs */}
                        <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 sm:p-6 border`}>
                          <h3 className={`text-base sm:text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Key Performance Indicators
                          </h3>
                          {renderDepartmentKPIs(selectedKPI.kpis)}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className={`mt-6 sm:mt-8 pt-4 sm:pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
                        <div className="flex flex-wrap gap-2 sm:space-x-4">
                          {selectedKPI.hasCustomizations && !isNewTemplate && (
                            <button
                              onClick={handleResetToTemplate}
                              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              Reset to Template
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                          {!isNewTemplate && (
                            <button
                              onClick={() => setIsModalOpen(false)}
                              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                            >
                              Close
                            </button>
                          )}
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleCancel}
                                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                              >
                                Cancel
                              </button>
                              <SaveButton onClick={handleSave} />
                            </>
                          ) : (
                            <EditButton onClick={handleEdit} />
                          )}
                        </div>
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error rendering KPI modal:', error);
                  return (
                    <div className="text-center py-8">
                      <h2 className="text-xl font-semibold text-red-600 mb-4">Error Loading KPI</h2>
                      <p className="text-gray-600 mb-4">There was an error loading this KPI. Please try again.</p>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Close
                      </button>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartKPIs;

